// cart.js — localStorage-backed shop cart for the /shop flow.
//
// Line shape:
//   { lineId, squareItemId, variationId, name, variantName, priceCents,
//     currency, imageUrl, quantity, isPOD }
//
// lineId = `${squareItemId}::${variationId}` — same item + variant collapses
// into one line with quantity bumped rather than a duplicate row.
//
// Cart state is global per-browser (no user login required). Listeners across
// tabs sync via the `storage` event; same-tab sync uses a custom event since
// `storage` doesn't fire for the tab that wrote the value.

import { useEffect, useState } from "react";

const CART_KEY = "jop_shop_cart_v1";
const CART_EVENT = "jop-cart-changed";

export function readCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  try { window.dispatchEvent(new CustomEvent(CART_EVENT)); } catch {}
}

export function addToCart({ squareItemId, variationId, name, variantName, priceCents, currency, imageUrl, isPOD, quantity = 1 }) {
  const lineId = `${squareItemId}::${variationId}`;
  const qtyToAdd = Math.max(1, Math.floor(quantity));
  const cart = readCart();
  const existing = cart.find(l => l.lineId === lineId);
  if (existing) {
    existing.quantity += qtyToAdd;
  } else {
    cart.push({
      lineId,
      squareItemId,
      variationId,
      name,
      variantName: variantName || null,
      priceCents,
      currency: currency || "USD",
      imageUrl: imageUrl || null,
      quantity: qtyToAdd,
      isPOD: !!isPOD,
    });
  }
  writeCart(cart);
  return cart;
}

export function removeFromCart(lineId) {
  writeCart(readCart().filter(l => l.lineId !== lineId));
}

export function updateQuantity(lineId, quantity) {
  const qty = Math.max(0, Math.floor(quantity));
  const next = readCart()
    .map(l => l.lineId === lineId ? { ...l, quantity: qty } : l)
    .filter(l => l.quantity > 0);
  writeCart(next);
}

export function clearCart() { writeCart([]); }

export function cartSubtotalCents(cart = readCart()) {
  return cart.reduce((sum, l) => sum + ((l.priceCents || 0) * (l.quantity || 0)), 0);
}

export function cartCount(cart = readCart()) {
  return cart.reduce((sum, l) => sum + (l.quantity || 0), 0);
}

// Subscribe to changes (same-tab + cross-tab). Returns an unsubscribe fn.
export function subscribeCart(handler) {
  const onLocal = () => handler(readCart());
  const onStorage = (e) => { if (e.key === CART_KEY) handler(readCart()); };
  window.addEventListener(CART_EVENT, onLocal);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CART_EVENT, onLocal);
    window.removeEventListener("storage", onStorage);
  };
}

export function formatPrice(cents, currency = "USD") {
  if (cents == null || isNaN(cents)) return "—";
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(dollars);
}

// React hook — any component can subscribe to live cart state.
// Re-renders on add/remove/qty-change, and syncs across tabs.
export function useCart() {
  const [items, setItems] = useState(() => readCart());
  useEffect(() => subscribeCart(setItems), []);
  return items;
}
